import React, { useEffect, useState, useRef } from 'react';
import Card from 'components/card';
import { getProductForecast, getTopProductsForecast } from 'interfaces/forecasting';
import { getProducts } from 'interfaces/products';
import LineChart from 'components/charts/LineChart';
import { MdTrendingUp, MdWarning, MdShowChart, MdTimeline, MdFunctions } from 'react-icons/md';
import TooltipHorizon from 'components/tooltip';
import MiniStatistics from 'components/card/MiniStatistics';
import SearchIcon from 'components/icons/SearchIcon';

const ForecastingView = () => {
    const [products, setProducts] = useState<any[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<string>('');
    const [selectedProductName, setSelectedProductName] = useState<string>('');
    const [period, setPeriod] = useState<'week' | 'month'>('week');
    const [forecastData, setForecastData] = useState<any>(null);
    const [topProducts, setTopProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetchInitialData = async () => {
            const res = await getProducts(1, 1000);
            if (res && res.data) {
                setProducts(res.data);
                if (res.data.length > 0) {
                    setSelectedProduct(res.data[0]._id);
                    setSelectedProductName(res.data[0].name);
                }
            }
        };
        fetchInitialData();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const fetchTopForecast = async () => {
            const res = await getTopProductsForecast(period);
            if (res) {
                setTopProducts(res);
            }
        }
        fetchTopForecast();
    }, [period]);

    const filteredProducts = products.filter(p => 
        p.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        if (selectedProduct) {
            fetchForecast();
        }
    }, [selectedProduct, period]);

    const fetchForecast = async () => {
        setLoading(true);
        const data = await getProductForecast(selectedProduct, period);
        setForecastData(data);
        setLoading(false);
    };

    const chartOptions = {
        chart: {
            toolbar: { show: false },
        },
        tooltip: {
            theme: 'dark',
            x: { show: true }
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth' as const },
        xaxis: {
            categories: forecastData?.history?.map((h: any) => h.date) || [],
            labels: {
                style: { colors: '#A3AED0', fontSize: '12px', fontWeight: '500' },
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            labels: {
                style: { colors: '#A3AED0', fontSize: '12px', fontWeight: '500' },
            },
        },
        legend: { show: true },
        grid: {
            borderColor: 'rgba(163, 174, 208, 0.3)',
            show: true,
            yaxis: { lines: { show: true } },
            xaxis: { lines: { show: false } },
        },
        colors: ['#0260CB', '#6AD2FF'], 
    };

    const chartSeries = [
        {
            name: "Ventes Historiques",
            data: forecastData?.history?.map((h: any) => h.quantity) || [],
        },
    ];

    return (
        <div className="mt-5 grid h-full grid-cols-1 gap-5">
            {/* Top Section: Controls & Mini Stats */}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
                {/* Product Selection Control */}
                <Card extra="p-4 w-full h-full col-span-1 md:col-span-2 lg:col-span-1">
                    <div className="flex flex-col h-full justify-between gap-4">
                        <div>
                            <h4 className="text-lg font-bold text-navy-700 dark:text-white mb-2">
                                Produit à analyser
                            </h4>
                            <div className="relative" ref={dropdownRef}>
                                <div className="flex h-[40px] items-center rounded-xl bg-lightPrimary text-sm text-gray-600 dark:bg-navy-900 dark:text-white">
                                    <SearchIcon />
                                    <input
                                        type="text"
                                        placeholder={selectedProductName || "Rechercher un produit..."}
                                        className="block h-full w-full rounded-xl bg-lightPrimary text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark:placeholder:!text-white"
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setShowDropdown(true);
                                        }}
                                        onFocus={() => setShowDropdown(true)}
                                    />
                                </div>
                                {showDropdown && filteredProducts.length > 0 && (
                                    <div className="absolute z-10 mt-1 w-full max-h-[300px] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg dark:bg-navy-800 dark:border-navy-700">
                                        {filteredProducts.map((p) => (
                                            <div
                                                key={p._id}
                                                className={`cursor-pointer px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-navy-700 ${
                                                    selectedProduct === p._id ? 'bg-blue-50 dark:bg-navy-700' : ''
                                                }`}
                                                onClick={() => {
                                                    setSelectedProduct(p._id);
                                                    setSelectedProductName(p.name);
                                                    setSearchTerm('');
                                                    setShowDropdown(false);
                                                }}
                                            >
                                                <p className="font-medium text-navy-700 dark:text-white">{p.name}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex bg-lightPrimary rounded-xl p-1 dark:bg-navy-900">
                             <button
                                className={`flex-1 rounded-lg py-2 text-sm font-bold transition-all ${
                                    period === 'week' 
                                    ? 'bg-[#0260CB] text-white shadow-md' 
                                    : 'text-gray-600 hover:bg-white/50 dark:text-white'
                                }`}
                                onClick={() => setPeriod('week')}
                            >
                                Semaine
                            </button>
                            <button
                                className={`flex-1 rounded-lg py-2 text-sm font-bold transition-all ${
                                    period === 'month' 
                                    ? 'bg-[#0260CB] text-white shadow-md' 
                                    : 'text-gray-600 hover:bg-white/50 dark:text-white'
                                }`}
                                onClick={() => setPeriod('month')}
                            >
                                Mois
                            </button>
                        </div>
                    </div>
                </Card>

                {/* Stats Cards */}
                <div className="col-span-1 md:col-span-2 lg:col-span-3 flex flex-col gap-5">
                     {forecastData?.forecast ? (
                        <>
                            {/* Prévision Recommandée - Full Width */}
                            <MiniStatistics
                                name="Prévision Recommandée"
                                value={`${forecastData.forecast.recommended} U`}
                                icon={<MdTrendingUp color="#0260CB" />}
                                iconBg="bg-lightPrimary"
                            />
                            
                            {/* Trois Moyennes en dessous */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                <TooltipHorizon
                                    trigger={
                                        <div className="cursor-help h-full">
                                            <MiniStatistics
                                                name="Moyenne Mobile (SMA)"
                                                value={`${forecastData.forecast.sma}`}
                                                icon={<MdShowChart color="#0260CB" />}
                                                iconBg="bg-lightPrimary"
                                            />
                                        </div>
                                    }
                                    content={<div className="p-2"><p className="font-bold mb-1 text-base">SMA (Simple Moving Average)</p><p className='text-xs font-medium'>Moyenne simple des ventes passées.</p></div>}
                                    placement="top"
                                    extra=""
                                />
                                <TooltipHorizon
                                    trigger={
                                        <div className="cursor-help h-full">
                                            <MiniStatistics
                                                name="Moyenne Pondérée (WMA)"
                                                value={`${forecastData.forecast.wma}`}
                                                icon={<MdTimeline color="#0260CB" />}
                                                iconBg="bg-lightPrimary"
                                            />
                                        </div>
                                    }
                                    content={<div className="p-2"><p className="font-bold mb-1 text-base">WMA (Weighted Moving Average)</p><p className='text-xs font-medium'>Donne plus de poids aux ventes récentes.</p></div>}
                                    placement="top"
                                    extra=""
                                />
                                <TooltipHorizon
                                    trigger={
                                        <div className="cursor-help h-full">
                                            <MiniStatistics
                                                name="Régression Linéaire"
                                                value={`${forecastData.forecast.linear}`}
                                                icon={<MdFunctions color="#0260CB" />}
                                                iconBg="bg-lightPrimary"
                                            />
                                        </div>
                                    }
                                    content={<div className="p-2"><p className="font-bold mb-1 text-base">Régression Linéaire</p><p className='text-xs font-medium'>Projection basée sur la tendance globale.</p></div>}
                                    placement="top"
                                    extra=""
                                />
                            </div>
                        </>
                    ) : (
                        <Card extra="w-full p-4 flex items-center justify-center text-gray-500">
                           <p>Sélectionnez un produit pour voir les prévisions</p>
                        </Card>
                    )}
                </div>
            </div>

            {/* Main Content: Chart & Detail Table */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                <Card extra="w-full p-4 h-[400px]">
                    <div className="mb-4">
                        <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                            Évolution des ventes
                        </h4>
                    </div>
                    {forecastData && (
                        <div className="h-full w-full">
                            <LineChart chartOptions={chartOptions} chartData={chartSeries} />
                        </div>
                    )}
                </Card>

                <Card extra="w-full p-4 h-[400px] overflow-hidden">
                    <div className="mb-4">
                        <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                            Historique des commandes
                        </h4>
                    </div>
                    <div className="overflow-y-auto h-full pb-8">
                         <table className="w-full">
                            <thead>
                                <tr>
                                    <th className="border-b border-gray-200 pr-14 pb-[10px] text-start dark:!border-navy-700">
                                        <div className="flex w-full justify-between pr-10 text-xs tracking-wide text-gray-600">
                                            PERIODE
                                        </div>
                                    </th>
                                    <th className="border-b border-gray-200 pr-14 pb-[10px] text-start dark:!border-navy-700">
                                        <div className="flex w-full justify-between pr-10 text-xs tracking-wide text-gray-600">
                                            QUANTITÉ VENDUE
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {forecastData?.history?.slice().reverse().map((row: any, index: number) => (
                                    <tr key={index}>
                                        <td className="py-2 text-sm font-bold text-navy-700 dark:text-white">
                                            {row.date}
                                        </td>
                                        <td className="py-2 text-sm font-bold text-navy-700 dark:text-white">
                                            {row.quantity}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>

            {/* Top 10 Table */}
            <Card extra="w-full p-4">
                <div className="mb-8 flex items-center justify-between">
                    <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                        Top 10 Produits les plus commandés ({period === 'week' ? 'Semaine' : 'Mois'})
                    </h4>
                </div>
                <div className="w-full overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr>
                                <th className="border-b border-gray-200 pr-20 pb-[10px] text-start dark:!border-navy-700">
                                    <p className="text-xs tracking-wide text-gray-600 font-bold uppercase">Produit</p>
                                </th>
                                <th className="border-b border-gray-200 pr-10 pb-[10px] text-start dark:!border-navy-700">
                                    <p className="text-xs tracking-wide text-gray-600 font-bold uppercase">Stock Actuel</p>
                                </th>
                                <th className="border-b border-gray-200 pr-10 pb-[10px] text-start dark:!border-navy-700">
                                    <p className="text-xs tracking-wide text-gray-600 font-bold uppercase">Ventes (30j)</p>
                                </th>
                                <th className="border-b border-gray-200 pr-10 pb-[10px] text-start dark:!border-navy-700">
                                    <p className="text-xs tracking-wide text-gray-600 font-bold uppercase">Prévision</p>
                                </th>
                                <th className="border-b border-gray-200 pr-10 pb-[10px] text-start dark:!border-navy-700">
                                    <p className="text-xs tracking-wide text-gray-600 font-bold uppercase">État</p>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {topProducts.length > 0 ? topProducts.map((row: any, index: number) => (
                                <tr key={index}>
                                    <td className="py-3 text-sm font-bold text-navy-700 dark:text-white">
                                        {row.name}
                                    </td>
                                    <td className="py-3 text-sm font-bold text-navy-700 dark:text-white">
                                        {row.currentStock} {row.unity}
                                    </td>
                                    <td className="py-3 text-sm font-bold text-navy-700 dark:text-white">
                                        {row.salesLast30Days}
                                    </td>
                                    <td className="py-3 text-sm font-bold text-[#0260CB] dark:text-white">
                                        {row.forecastNextWeek}
                                    </td>
                                    <td className="py-3">
                                       {row.currentStock < row.forecastNextWeek ? (
                                             <div className="flex items-center text-red-500 text-sm font-bold gap-1">
                                                <MdWarning />
                                                <span>Critique</span>
                                             </div>
                                       ) : (
                                            <div className="flex items-center text-green-500 text-sm font-bold gap-1">
                                                <span>OK</span>
                                            </div>
                                       )}
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={5} className="py-4 text-center text-gray-500">Chargement...</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
};

export default ForecastingView;
